import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiRequestService } from '@common/services/api-request.service';
import { environment } from '../../environments/environment';
import type { ValidationResult, ProcessResponse } from './mock-import.service';

@Injectable({
  providedIn: 'root'
})
export class ImportService {

  
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient, private apiRequest: ApiRequestService) {}

  validateFile(file: File): Observable<ValidationResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ValidationResult>(`${this.baseUrl}/meters/import/validate`, form)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const message = err?.error?.message || err?.message || 'File validation failed';
          return throwError(() => new Error(message));
        })
      );
  }

  importFile(file: File): Observable<ProcessResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ProcessResponse>(`${this.baseUrl}/meters/import`, form)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const message = err?.error?.message || err?.message || 'Import failed';
          return throwError(() => new Error(message));
        })
      );
  }

  // Start async import job
  startImport(file: File): Observable<any> {
    const form = new FormData();
    form.append('file', file);
    return this.apiRequest
      .apiRequest('utilities/import_devices/start', '', form)
      .pipe(
        catchError((err: any) => {
          const dataFromError = Array.isArray(err?.data)
            ? err.data
            : Array.isArray(err?.error?.data)
              ? err.error.data
              : undefined;
          const messageFromData = dataFromError?.find((msg: unknown) => typeof msg === 'string');
          const message =
            (typeof err === 'string' ? err : null)
            || (typeof messageFromData === 'string' ? messageFromData : null)
            || (typeof err?.message === 'string' ? err.message : null)
            || (typeof err?.error?.message === 'string' ? err.error.message : null)
            || 'Failed to start import';

          const normalizedError: any = typeof err === 'object' && err !== null ? { ...err } : {};
          normalizedError.message = message;

          if (!normalizedError.data && dataFromError) {
            normalizedError.data = dataFromError;
          }

          if (normalizedError.error) {
            normalizedError.error = { ...normalizedError.error, message, data: normalizedError.error.data ?? dataFromError };
          } else {
            normalizedError.error = dataFromError || message !== 'Failed to start import'
              ? { message, data: dataFromError }
              : undefined;
          }

          return throwError(() => normalizedError);
        })
      );
  }

  // Poll async import job status; when process=true backend processes next chunk
  getImportStatus(jobId: string, process = false): Observable<any> {
    const body: Record<string, any> = {
      payload: {
        userID: localStorage.getItem('userID'),
        jobId
      }
    };

    if (process) {
      body.payload.process = 1;
    }

    return this.apiRequest
      .apiRequest('common/report_job_status', '', body, { cancelable: false })
      .pipe(
        map((response: any) => (response && response.data ? response.data : response)),
        catchError((err: any) => {
          const message = err?.error?.message || err?.message || 'Failed to fetch import status';
          return throwError(() => new Error(message));
        })
      );
  }
}

